import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faExclamationTriangle ,faTrash, faX, faPlusCircle, faChevronDown, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import '../Css/Animation.css';
import { Spinner } from '../Loader/Loader';
import { Msg200or201, Msg204, Msg400, Msg401 ,Msg403, Msg500 } from '../Messages/Messages';
import { useNavigate, useLocation } from 'react-router-dom';
import useCommonResponseHandler from '../CommenResponseAndErrorHandlers/CommenResponseHandler';
import { validate } from '../CommenResponseAndErrorHandlers/commenUiErrorHandlers';
import { decryptData } from '../../utils/storageCrypto';
//import Dropdown from './DropDown'
// Custom hook to handle outside clicks
const useOutsideClick = (ref, callback) => {
  const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
          callback();
      }
  };

  useEffect(() => {
      document.addEventListener('click', handleClick);
      return () => {
          document.removeEventListener('click', handleClick);
      };
  }, []);
};
 
const CustomDropdown = ({ options, selected, onSelect,showAddNew, widthClass = 'w-1/4' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
  
    const filteredOptions = options.filter(option => {
      if (!option || !option.label) return false;
      return option.label.toLowerCase().includes(searchTerm.toLowerCase());
    });
  
    const handleOptionClick = (value, label, id) => {
      onSelect(value, label, id);
      setIsOpen(false);
      setFocusedOptionIndex(null);
    };
  
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) { 
            setIsOpen(true);
          }
          setFocusedOptionIndex(prevIndex => {
            const newIndex = prevIndex === null ? 0 : Math.min(prevIndex + 1, filteredOptions.length - 1);
            scrollToOption(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) { 
            setIsOpen(true);
          }
          setFocusedOptionIndex(prevIndex => {
            const newIndex = prevIndex === null ? filteredOptions.length - 1 : Math.max(prevIndex - 1, 0);
            scrollToOption(newIndex);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedOptionIndex !== null) {
            const selectedOption = filteredOptions[focusedOptionIndex];
            handleOptionClick(selectedOption.value, selectedOption.label, selectedOption.id);
          }
          break;
        default:
          break;
      }
    };
  
    const scrollToOption = (index) => {
      const optionElements = dropdownRef.current.querySelectorAll('div[data-index]');
      if (optionElements && optionElements[index]) {
        optionElements[index].scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth',
        });
      }
    };
  
    useOutsideClick(dropdownRef, () => setIsOpen(false));
    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen]);
  

  return (
    <div className={`relative ${widthClass}`} ref={dropdownRef}>
    <div
      className="flex justify-between items-center p-2 border bg-white border-gray-300 rounded-md cursor-pointer"
      onClick={() => setIsOpen(!isOpen)}
    >
      <span className={`text-sm ${selected ? 'text-black' : 'text-[#838195]'}`}>
        {selected && selected.label || 'Select'}
      </span>
      <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 text-gray-500" />
    </div>
    {isOpen && (
      <div className="absolute mt-1 w-full max-h-40 bg-white border border-gray-300 rounded-md shadow-md z-10 flex flex-col">
        <input
          type="text"
          className="p-1 border rounded-md border-blue-500 focus:outline-none hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus={isOpen}
        />
        <div className="overflow-y-auto scrollbar-visible flex-grow">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option,index) => (
              <div
                key={option.value}
                data-index={index}
                className={`p-2 cursor-pointer text-sm ${index === focusedOptionIndex ? 'bg-blue-500 text-white' : ''} hover:bg-blue-500 hover:text-white`}
                onClick={() => handleOptionClick(option.value, option.label,option.id)}
              >
                {option.icon && <FontAwesomeIcon icon={option.icon} className="mr-2" />}
                {option.label}
              </div>
            ))
          ) : (
            <div className="p-2 text-sm text-gray-500">
              Not available
            </div>
          )}
        </div>
        {showAddNew && (
          <button
          type="button"
          className="flex items-center border-t p-2 mt-2 text-blue-500 "
          onClick={showAddNew}
        >
          <FontAwesomeIcon icon={faPlusCircle} className="w-4 h-4 mr-2" />
           New UOM
        </button>
        )}
      </div>
    )}
  </div>
  );
};

const InputWithUnit = ({ value, onChange, unit, onUnitChange, units }) => {
  return (
    <div className="flex w-full max-w-[220px]">
      <style>{`
        .no-spinner-input::-webkit-outer-spin-button,
        .no-spinner-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .no-spinner-input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      
      {/* Input */}
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '' || val === '-') {
            onChange('');
          } else {
            const num = Number(val);
            if (!isNaN(num) && num >= 0) {
              onChange(num);
            }
            // else ignore invalid input
          }
        }}
        placeholder="Enter"
        className="no-spinner-input w-2/3 px-3 py-2 text-sm border border-gray-300 rounded-l-md 
                   focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 appearance-none"
      />

      {/* Dropdown */}
      <select
        value={unit}
        onChange={(e) => onUnitChange(e.target.value)}
        className="w-1/3 px-2 py-2 text-sm border border-l-0 border-gray-300 
                   rounded-r-md bg-white text-gray-700 cursor-pointer
                   focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
      >
        {units.map((u) => (
          <option key={u.value} value={u.value}>
            {u.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const EditItems = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ItemData } = location.state;
  console.log(ItemData);
  const [ProductSubCategory, setProductSubCategory] = useState([{product_sub_category_name: '', UOM_type: '', description: '', UOM_id: '' }]);
  const [productCategories, setProductCategories] = useState([]);
  const [selectedProductCategory, setSelectedProductCategory] = useState(null);
  const [productSubCategories, setProductSubCategories] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [showNewUOMPopup, setShowNewUOMPopup] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [includeAllVendors, setIncludeAllVendors] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  //console.log(selectedGroup)
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [selectedUOM, setSelectedUOM] = useState();
  const [selectedTax, setSelectedTax] = useState('');
  const [selectedMode,setSelectedMode] = useState('');
  const [SelectdPCUomid,setSelectdPCUomid] = useState()
  const [UOM, setUOM] = useState('')
  const [description,setdescription]= useState('')
  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET
   const [UomIndex,setUomIndex] = useState()
 const { 
  showPopup, popupMessage, popupType, retryFunction,
  isSaving ,setIsLoading,setPopupMessage,setPopupType,handleGetResponse, handleSaveResponse, setShowPopup,
  setRetryFunction, handleDeleteResponse, setIsSaving  } = useCommonResponseHandler();
  const RETRY_LIMIT = 3; // Maximum number of retries
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const [roleId, setRoleId] = useState(null);
  const [alternativeName, setAlternativeName] = useState("");

const [quoteThresholdQuantity, setQuoteThresholdQuantity] = useState('');




 const[ uomOptions ,setUomOptions]= useState([

]);
const [errors, setErrors] = useState({});
const [showQuantityPricingPopup, setShowQuantityPricingPopup] = useState(false);
const [quantityPricingRows, setQuantityPricingRows] = useState([
  { minQuantity: '', maxQuantity: '', sellingPrice: '', costPrice: '' }
]);

const Taxoptions = [
  { label: 'Taxable', value: 'Taxable' },
  { label: 'Non-Taxable', value: 'Non-Taxable' },
  { label: 'Non-GST supply', value: 'Non-GST Supply' }
];

const PurchaseMode = [
  { label: 'Quote', value: 'Quote' },
  { label: 'Instant Buy', value: 'Instant Buy' },
  { label: 'Both', value: 'Both' }
];

const dimensionUnits = [
  { label: 'mm', value: 'mm' },
  { label: 'cm', value: 'cm' },
  { label: 'm', value: 'm' },
  { label: 'inch', value: 'inch' },
  { label: 'ft', value: 'ft' }
];

const weightUnits = [
  { label: 'kg', value: 'kg' },
  { label: 'g', value: 'g' },
  { label: 'lb', value: 'lb' }
];

const vehicleClasses = [
  { label: 'Bike', value: 'BIKE' },
  { label: 'Auto', value: 'AUTO' },
  { label: 'Ace', value: 'ACE' }
];

const handlingClasses = [
  { label: 'Normal', value: 'NORMAL' },
  { label: 'Long', value: 'LONG' },
  { label: 'Bagged', value: 'BAGGED' },
  { label: 'Fragile', value: 'FRAGILE' },
  { label: 'Heavy', value: 'HEAVY' }
];  

const normalizeTaxPreference = (apiValue) => {
  if (!apiValue) return null;
  const normalized = apiValue.toLowerCase().replace(/[\s-]/g, '');
  if (normalized === 'taxable') return 'Taxable';
  if (normalized === 'nontaxable' || normalized === 'non-taxable') return 'Non-Taxable';
  if (normalized === 'nongstsupply' || normalized === 'non-gstsupply') return 'Non-GST Supply';
  return apiValue;
};

const normalizePurchaseMode = (apiValue) => {
  if (!apiValue) return null;
  const normalized = apiValue.toLowerCase().replace(/[\s-]/g, '');
  if (normalized === 'both') return 'Both';
  if (normalized === 'quote' || normalized === 'Quote') return 'Quote';
  if (normalized === 'Instant Buy' || normalized === 'instant buy') return 'Instant Buy';
  return apiValue;
};

const [imagePreview, setImagePreview] = useState(null);
const [rows, setRows] = useState([{ SNo: '', Name: '', Value: ''}]);
const [specifications, setSpecifications] = useState([{name:'', value:'' }]);
const [image,setImage] = useState('');
const imageUrl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;
const [data, setData] = useState({
 // product_group_id: null,
  vendor_id: '',
  product_category_id: '',
  product_name: '',
  product_image: '',
  product_description : '',
  selling_price:'',
  cost_price:'',
  specifications, 
  product_sub_category_id: '',
  UOM: '',
  tax_preference: '',
  GST: '',
  IGST: '',
  SKU: '',
  HSN_Code: '',
  is_buildcoins_applicable: false,
  buildcoins_max_quantity_applicable:'',
  buildcoins_max_discount:'',
  alternate_names: '',
  market_range:{
    min:'',
    max:''
  },
  quantity_limits:{
    min:'',
    max:''
  },
  dimensions: {
    length: '',
    width: '',
    height: '',
    unit: 'cm'
  },
  weight: {
    value: '',
    unit: 'kg'
  }
});
const [bcappStatus, setBcAppStatus] = useState("no");
const [isDeliveredByBrickbucket, setIsDeliveredByBrickbucket] = useState(true);
const [isCODAvailable, setIsCODAvailable] = useState(false);
const [isTaxIncluded,setIsTaxIncluded] = useState(true);
const [marketRangeError, setMarketRangeError] = useState('');
const [quantityLimitsError, setQuantityLimitsError] = useState('');
const [minimumVehicleClass,setMinimumVehicleClass] = useState('');
const [handlingClass,setHandlingClass] = useState('');
const [fastDeliveryEligible,setFastDeliveryEligible] = useState(false);
const [maxFastDeliveryQty,setMaxFastDeliveryQty] = useState();

const isPricingInvalid =
  Number(data.cost_price) > 0 &&
  Number(data.selling_price) > 0 &&
  Number(data.cost_price) > Number(data.selling_price);
/* const isPricingInvalid2 = 
  Number(data.mrp) > 0 &&
  Number(data.cost_price) > 0 &&
  Number(data.mrp) > Number(data.cost_price); */



const handleBcAppChange = (e) => {
  const selectedValue = e.target.value;  // "yes" or "no"
  setBcAppStatus(selectedValue);
  
  // Map to boolean and update data state
  const isApplicable = selectedValue === "yes";
  setData(prev => ({ ...prev, is_buildcoins_applicable: isApplicable }));
  
  console.log("Selected:", selectedValue, "Boolean:", isApplicable);
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    setImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  }
};

 

  const handleDropdownChange = (value ,label, id) => {
    if (value === 'add-new') {
      setShowNewUOMPopup(true);
    } else {
      setSelectedUOM({value, label , _id:id});
      setSelectdPCUomid(id)
    }
  };

  const handleTaxDropdownChange = (value, label) => {
   // console.log('Selected Tax label:', label); // Debugging: Check selected value
    // Keep selectedTax as an object so the CustomDropdown can display .label
    setSelectedTax({ value, label });
  };
  
  const handleModeDropdownChange = (value, label) => {
   // console.log('Selected Mode label:', label); // Debugging: Check selected value
    // Keep selectedMode as an object so the CustomDropdown can display .label
    setSelectedMode({ value, label });
  };

    const handleHandlingClass = (value,label) => {
    setHandlingClass({value, label})
  };

  const handleMinimumVehicleClass = (value,label) => {
    setMinimumVehicleClass({value, label})
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setShowNewUOMPopup(false)
    setUOM('');
    setdescription('');
  };



  const deleteRow = (index) => {
    const updatedData = [...rows];
    updatedData.splice(index, 1);
    setRows(updatedData);
  };

  const sortArray = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });

    const sortedArray = [...ProductSubCategory].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === 'ascending' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    setProductSubCategory(sortedArray);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
    }
    return null;
  };


  const handleSubmitUomData = async (attempt = 1) => {
    const errorMessages = [];
    if (!UOM) {
      errorMessages.push('Please enter UOM');
    }
    if (errorMessages.length > 0) {
      setErrorMessages(errorMessages);
      return;
    }
    setIsSaving(true);
    try {
          const encryptedToken = sessionStorage.getItem('token');
              const token = decryptData(encryptedToken);

      const saveUomUrl = `${fetchUrl}UOMs/create-UOM`;
      const requestData = {
        UOM : UOM,
        UOM_description : description,
      };
   //  console.log(requestData)
      const response = await fetch(saveUomUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
        body: JSON.stringify(requestData),
      });
      const data = await response.json();
      const output = await handleSaveResponse(response , data)
      if (output) {       
       // console.log(data.message);
        setShowPopup(false);
        setPopupMessage('UOM Created sucessfully')
        setShowNewUOMPopup(false);
        setUOM('')
        setdescription('')
        getUoms()
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error('Failed to fetch:', error);

      if (attempt < RETRY_LIMIT) {
        setRetryCount(attempt + 1); // Increment retry count
        setTimeout(() => handleSubmitUomData(attempt + 1), 1000); // Retry after 1 second
      } else {
        setIsLoading(false);
        setPopupMessage('Internal Server Error. Kindly Retry.');
        setPopupType('500');
        setShowPopup(true);
        setRetryFunction(() => () => handleSubmitUomData(attempt)); // Set retry function
      }
    }
  };

  const getUoms = async (attempt = 1) => {
    try {
          const encryptedToken = sessionStorage.getItem('token');
              const token = decryptData(encryptedToken);

      const getUomUrl = `${fetchUrl}UOMs/get-all-UOMs`;
  
      const response = await fetch(getUomUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
      });
      const data = await response.json();
      const output = await handleGetResponse(response , data)
      if (output) {
       
        const AllUoms= []
        for ( const uom of output){
          const uomData= {
            label: uom.UOM, value: uom._id , id: uom._id
          }
          AllUoms.push(uomData)
          
        }
        const addUomOption = []
        const UOMS= [...AllUoms , ...addUomOption]
       // console.log(UOMS)
        setUomOptions(UOMS)
      } else {
      
        console.error('Error fetching UOMs:', data.message);
      }
    } catch (error) {
      console.error('Failed to fetch:', error);

      if (attempt < RETRY_LIMIT) {
        setRetryCount(attempt + 1); // Increment retry count
        setTimeout(() => getUoms(attempt + 1), 1000); // Retry after 1 second
      } else {
        setIsLoading(false);
        setPopupMessage('Internal Server Error. Kindly Retry.');
        setPopupType('500');
        setShowPopup(true);
        setRetryFunction(() => () => getUoms(attempt)); // Set retry function
      }
    }
  };

  useEffect(() => {
    getUoms();
  }, []);
  



   const addRow = () => {
    setUomIndex()
    const newRow = {  SNo: '', Name:'' , Value : ''};
    setRows([...rows, newRow]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleUpdateProduct = async () => {
        const encryptedToken = sessionStorage.getItem('token');
            const token = decryptData(encryptedToken);
            const encryptedhubId = sessionStorage.getItem('hubId');
            const hubId = decryptData(encryptedhubId);

    setIsSaving(true);
    const _id = ItemData?._id;
    const validSpecifications = rows.filter(row => row.Name.trim() !== '' && row.Value.trim() !== '');
    const formattedSpecifications = validSpecifications.map(row => ({ [row.Name.trim()]: row.Value.trim() }));



    const formData = new FormData();
    if (_id) {
        formData.append('_id', _id);
    }
    formData.append('product_category_id', selectedProductCategory.value);
    formData.append('product_name', data.product_name);
    formData.append('product_descrption', data.product_descrption);
    formData.append('specifications', JSON.stringify(formattedSpecifications));
    formData.append('selling_price', data.selling_price);
    formData.append('cost_price', data.cost_price);
    formData.append('vendor_id', selectedVendor.value);
    formData.append('is_delivered_by_brickbucket', isDeliveredByBrickbucket);
    formData.append('is_COD_available', isCODAvailable);
    formData.append('mrp', data.mrp);
    formData.append('discount', data.discount);
    formData.append('tax_included',isTaxIncluded);

    if(data.margin_percent && data.margin_percent > 0){
   formData.append('margin_rate', data.margin_percent);
    }

    console.log('Selected Group Data:', selectedGroup);

  if (selectedGroup && selectedGroup.value && selectedGroup.value !== '') {
  formData.append('product_group_id', selectedGroup.value);
  console.log('Added product_group_id to formData:', selectedGroup.value);
} else {
  formData.append('product_group_id', '');
  console.log('No valid group selected, appending empty product_group_id');
  }
  console.log('FormData product_group_id value:', formData.get('product_group_id'));
  //formData.append('product_image', image);
  //const formData = new FormData();
  formData.append('image', image);


      //  if (image) {
      //    formData.append('product_image', image);
      // } else if (ItemData.product_image) {
      //   formData.append('product_image', ItemData.product_image);
      //  }    
      
    formData.append('product_description', data.product_description || '');
    formData.append('product_sub_category_id', selectedSubCategory?.value || '');

   // if (SelectdPCUomid) {
      console.log("SelectdPCUomid:",SelectdPCUomid)
        formData.append('UOM_id', SelectdPCUomid);
    //  }
      
      
    // Ensure we send the raw value (string) for tax preference
    const taxPreferenceValue = selectedTax?.value || '';
    const modeValue = selectedMode?.value || '';
    formData.append('tax_preference', taxPreferenceValue);
    formData.append('GST', data.GST);
    formData.append('IGST', data.IGST || '');
    formData.append('purchase_mode',modeValue);
    formData.append('SKU', data.SKU);
    formData.append('HSN_Code', data.HSN_Code);
    formData.append('hubId', hubId);
    formData.append('is_buildcoins_applicable',data.is_buildcoins_applicable === undefined ? false : data.is_buildcoins_applicable);
    formData.append('buildcoins_max_discount',data.buildcoins_max_discount);
    formData.append('buildcoins_max_quantity_applicable',data.buildcoins_max_quantity_applicable);  
    formData.append('alternate_names', JSON.stringify(data.alternate_names.split(',').map(name => name.trim()).filter(name => name)));
    formData.append('market_range', JSON.stringify({min: data.market_range.min || null, max: data.market_range.max || null}));
    formData.append('quantity_limits', JSON.stringify({min: data.quantity_limits.min || null, max: data.quantity_limits.max || null}));
     if (selectedMode?.value === 'Both') {
    formData.append('quote_threshold_quantity', quoteThresholdQuantity || '');
  }
    formData.append('dimensions', JSON.stringify(data.dimensions));
    formData.append('weight', JSON.stringify(data.weight));
    formData.append('fastDeliveryEligible',fastDeliveryEligible);
    formData.append('maxFastDeliveryQty',maxFastDeliveryQty);
    formData.append('minimumVehicleClass',minimumVehicleClass);
    formData.append('handlingClass',handlingClass);

    try {
      const updateProductUrl = `${fetchUrl}product/update-product`;
  
      const response = await fetch(updateProductUrl, {
        method: 'PUT',
        headers: {
          'x-token': token,
        },
        body: formData,
      });
  
      const responseData = await response.json();
      if (responseData.status === 200 || responseData.status === 201) {
        setIsSaving(false);
        setPopupMessage("Item updated successfully");
        setPopupType('200or201');
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
          navigate('/admin/app/items/itemslist');
        }, 3000);
      } else if (responseData.status === 204) {
        setIsSaving(false);
        setPopupMessage("Item updated successfully");
        setPopupType('204');
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
          navigate('/admin/app/items/itemslist');
        }, 3000);
      
        } else if (responseData.status === 404) {          
           setIsSaving(false);
            setPopupMessage(responseData.message);
             setPopupType("400"); 
           setShowPopup(true);
        } else{
        setErrorMessages([]);
        setIsSaving(false);
        setPopupMessage(responseData.message);
        setPopupType(`${responseData.status}`);
        setShowPopup(true);
      } 
    } catch (error) {
      //console.error('Failed to update product:', error);
      setShowPopup(true);
      //setPopupType('500');
     // setPopupMessage('Internal Server Error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  

  useEffect(() => {
    if (ItemData) {
      console.log('ItemData:', ItemData);
      setSelectedProductCategory({
        value: ItemData.product_category_id?._id,
        label: ItemData.product_category_id?.product_category_name
      });
      
      fetchSubProductCategories(ItemData.product_category_id?._id, includeAllVendors);
  
      if (ItemData.product_sub_category_id) {
        setSelectedSubCategory({
          value: ItemData.product_sub_category_id?._id,
          label: ItemData.product_sub_category_id?.product_sub_category_name
        });
      }
  
      if (ItemData.vendor_id) {
        setSelectedVendor({
          value: ItemData.vendor_id?._id,
          label: ItemData.vendor_id?.name
        });
      }
      if (ItemData.product_group_id) {
        setSelectedGroup({
          value: ItemData.product_group_id._id,
          label: ItemData.product_group_id.product_group_name,
           id: ItemData.product_group_id._id,
          sellingPrice: ItemData.product_group_id.selling_price,
          costPrice: ItemData.product_group_id.cost_price,
          taxPreference: ItemData.product_group_id.tax_preference,
          GST: ItemData.product_group_id.GST,
          IGST: ItemData.product_group_id.IGST,
          mrp: ItemData.product_group_id.mrp,
          discount: ItemData.product_group_id.mrp,
          margin: ItemData.product_group_id.margin_rate
        });
      }
      
      const normalizedTaxValue = normalizeTaxPreference(ItemData.tax_preference);
      const taxOption = Taxoptions.find(option => option.value === normalizedTaxValue);
      if (taxOption) {
        setSelectedTax({ value: taxOption.value, label: taxOption.label });
      }

      const normalizedModeValue = normalizePurchaseMode(ItemData.purchase_mode);
      const PurchaseModes = PurchaseMode.find(option => option.value === normalizedModeValue);
      if (PurchaseModes) {
        setSelectedMode({ value: PurchaseModes.value, label: PurchaseModes.label });
      }

      setImagePreview(ItemData.product_image ? `${imageUrl}${ItemData.product_image}` : null);

    const cost = parseFloat(ItemData.cost_price) || 0;
    const selling = parseFloat(ItemData.selling_price) || 0;

    let marginPercent = "";
    if (cost > 0 && selling > 0) {
      marginPercent = Number(((selling - cost) / cost * 100).toFixed(2));
    }
console.log("item dataaaaaa:",ItemData)
      // Set other fields
      setData({
        product_name: ItemData.product_name,
        product_description: ItemData.product_description,
        selling_price: ItemData.selling_price,
        cost_price: ItemData.cost_price,
        margin_percent: marginPercent,
        mrp:ItemData.mrp,
        discount: ItemData.discount, 
        GST: ItemData.GST,
        IGST: ItemData.IGST,
        SKU: ItemData.SKU,
        HSN_Code: ItemData.HSN_Code,
        is_buildcoins_applicable: ItemData.is_buildcoins_applicable,
        buildcoins_max_quantity_applicable: ItemData.buildcoins_max_quantity_applicable,
        buildcoins_max_discount: ItemData.buildcoins_max_discount,
        alternate_names: ItemData.alternate_names ? (Array.isArray(ItemData.alternate_names) ? ItemData.alternate_names.join(', ') : ItemData.alternate_names) : '',
        market_range: {
          min: ItemData.market_range?.min || '',
          max: ItemData.market_range?.max || ''
        },
        quantity_limits: {
          min: ItemData.quantity_limits?.min || '',
          max: ItemData.quantity_limits?.max || ''
        },
        tax_preference: normalizeTaxPreference(ItemData.tax_preference) || '',
        purchase_mode: normalizePurchaseMode(ItemData.purchase_mode) || '',
        dimensions:ItemData.dimensions ? {
          length: ItemData.dimensions.length || '',
          width: ItemData.dimensions.width || '',
          height: ItemData.dimensions.height || '',
          unit: 'cm' || ItemData.dimensions.unit
        } : { length: '', width: '', height: '', unit: 'cm' },
        weight: ItemData.weight ? {
          value: ItemData.weight.value || '',
          unit: ItemData.weight.unit || 'kg'
        } : { value: '', unit: 'kg' }
      });

      setBcAppStatus(ItemData.is_buildcoins_applicable ? "yes" : "no");  // true → "yes", false → "no"

      // Map delivery and COD flags from ItemData. Support multiple possible API field namings.
      const deliveredFlag = ItemData.is_delivered_by_brickbucket ?? ItemData.isDeliveredByBrickbucket ?? ItemData.isDeliveredByBrickBucket ?? ItemData.is_deliveredByBrickbucket;
      setIsDeliveredByBrickbucket(Boolean(deliveredFlag));

      const codFlag = ItemData.is_COD_available ?? ItemData.isCODAvailable ?? ItemData.isCodAvailable ?? ItemData.is_cod_available;
      setIsCODAvailable(Boolean(codFlag));

      const taxFlag = ItemData.tax_included ?? ItemData.tax_included ?? ItemData.tax_included ?? ItemData.tax_included;
      setIsTaxIncluded(Boolean(taxFlag))

      setMinimumVehicleClass(ItemData.minimumVehicleClass);
      setHandlingClass(ItemData.handlingClass);
      setFastDeliveryEligible(ItemData.fastDeliveryEligible);
      setMaxFastDeliveryQty(ItemData.maxFastDeliveryQty);

  console.log(ItemData.UOM_id)

      const UOM_Data = ItemData?.UOM_id;
      // Set UOM
      if (UOM_Data) {
        setSelectedUOM({
          value: UOM_Data._id,
          label: UOM_Data.UOM,
           _id: UOM_Data._id,
        });
        setSelectdPCUomid(UOM_Data._id)
      }

      console.log("selectedUOM",selectedUOM)
  
      // Set specifications
      if (ItemData.specifications) {
        const specs = JSON.parse(ItemData.specifications);
        setRows(specs.map((spec, index) => ({
          SNo: index + 1,
          Name: Object.keys(spec)[0],
          Value: Object.values(spec)[0]
        })));
      }

     setQuoteThresholdQuantity(ItemData.quote_threshold_quantity || '');

    }
  }, [ItemData]);
  
  
  useEffect(() => {
    fetchRoleId();
    fetchProductCategories();
  },[]);
  
  const fetchRoleId = () => {
    const encryptedroleId = sessionStorage.getItem('roleId');
    const sroleId = decryptData(encryptedroleId);
    setRoleId(sroleId);
  };

  const categoriesToDisplay = roleId === 6 
  ? productCategories.filter(category => (category?.value.toLowerCase()) === 'tiles')
  : productCategories;

  const fetchProductCategories = async (attempt = 1) => {
    try {
          const encryptedToken = sessionStorage.getItem('token');
              const token = decryptData(encryptedToken);

      const response = await fetch(fetchUrl + "product-category/get-all-productCategories", {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        }
      });
      const data = await response.json();
      const output = await handleGetResponse(response , data)
      //console.log(data);
      if (output) {
        const Categories = output.map(cat => ({
            id: cat._id,
            value: cat.product_category_name,
        label: cat.product_category_name,
          }));
        setProductCategories(Categories); // Update productCategories state
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error('Failed to fetch:', error);

      if (attempt < RETRY_LIMIT) {
        setRetryCount(attempt + 1); // Increment retry count
        setTimeout(() => fetchProductCategories(attempt + 1), 1000); // Retry after 1 second
      } else {
        setIsLoading(false);
        setPopupMessage('Internal Server Error. Kindly Retry.');
        setPopupType('500');
        setShowPopup(true);
        setRetryFunction(() => () => fetchProductCategories(attempt)); // Set retry function
      }
    }/* finally {
      setIsLoading(false);
    } */
  };
  

useEffect(() => {
  
}, [fetchUrl, selectedProductCategory]); // Ensure dependencies are correctly set


  
  const fetchSubProductCategories = async (product_category_id,includeAll = false,attempt = 1) => {
   // console.log('Fetching subcategories for ID:', product_category_id);
    //console.log(selectedProductCategory)
    try {
          const encryptedToken = sessionStorage.getItem('token');
              const token = decryptData(encryptedToken);
             
      const response = await fetch(fetchUrl + `product-sub-category/get-product-subCategories-and-vendors-by-productCategory?product_category_id=${product_category_id}` + (includeAll ? `&all=true` : ''), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token
        }
      });
      const data = await response.json();
      const output = await handleGetResponse(response , data)
       
      //console.log('Fetched Data:', data);
      if (output) {
        setProductSubCategories(output.subCategories);
       // Transform vendor data to match dropdown format
       const vendorOptions = output.AllAssignedVendors.map(vendor => ({
        value: vendor._id,
        label: vendor.vendor_type 
              ? `${vendor.vendor_name}  (${vendor.vendor_type})` 
              : (vendor.vendorType ? `${vendor.vendor_name} (${vendor.vendorType})` : vendor.vendor_name),
      }));
      setVendors(vendorOptions);
      console.log(output.AllAssignedVendors);
      } else {
        /* console.error(data.message); */
      }
    } catch (error) {
      console.error('Failed to fetch:', error);

      if (attempt < RETRY_LIMIT) {
        setRetryCount(attempt + 1); // Increment retry count
        setTimeout(() => fetchSubProductCategories(product_category_id,attempt + 1), 1000); // Retry after 1 second
      } else {
        setIsLoading(false);
        setPopupMessage('Internal Server Error. Kindly Retry.');
        setPopupType('500');
        setShowPopup(true);
        setRetryFunction(() => () => fetchSubProductCategories(product_category_id,attempt)); // Set retry function
      }
    }
  };

  

  useEffect(() => {
    if (selectedProductCategory && selectedVendor) {
      fetchProductGroups();
    }
  }, [selectedProductCategory, selectedVendor]);
  
   // Add dependencies to re-run the effect when these values change
  
  
  
const fetchProductGroups = async (attempt = 1) => {
    
     const encryptedToken = sessionStorage.getItem('token');
         const token = decryptData(encryptedToken);

  const product_category_id = selectedProductCategory?.value || '';
  const vendor_id = selectedVendor?.value || '';
  try {
    const response = await fetch(
      `${fetchUrl}product-groups/get-productGroups-by-vendor-and-productCategory?product_category_id=${product_category_id}&&vendor_id=${vendor_id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
      }
    );

    console.log('Fetching product groups...', response);

    const data = await response.json();
    console.log(data);
    const output = await handleGetResponse(response, data);
    console.log(output)
    if (output) {
        const ItemGroups = output.map(itemgroup => ({
          value: itemgroup._id,    
      label: itemgroup.product_group_name,
      id: itemgroup._id 
      
          }));
      setProductGroups(ItemGroups);
      console.log(ItemGroups);
    }
    console.log('Product groups:', data);
    // Handle the fetched data
  }catch (error) {
    console.error('Failed to fetch:', error);

    if (attempt < RETRY_LIMIT) {
      setRetryCount(attempt + 1); // Increment retry count
      setTimeout(() => fetchProductGroups(attempt + 1), 1000); // Retry after 1 second
    } else {
     // setIsLoading(false);
      setPopupMessage('Internal Server Error. Kindly Retry.');
      setPopupType('500');
      setShowPopup(true);
      setRetryFunction(() => () => fetchProductGroups(attempt)); // Set retry function
    }
  } finally {
   //setIsLoading(false);
  
  }
};


const handleProductCategorySelect = (value, label) => {
    setSelectedProductCategory({ value, label });
    // Additional logic if needed when a category is selected
  };
  

  const handleProductSubcategory = (value, label) => {
 
      setSelectedSubCategory({  value,  label });
   
      
  };
  
  const handleVendorSelect = (value, label) => {
   
      setSelectedVendor({  value,  label });
    
  
  };
  

/* const handleChange = (e) => {
  const { id, value } = e.target;
  if (id === 'cost_price' || id === 'selling_price') {
    setData((prevState) => ({ ...prevState, [id]: parseFloat(value) }));
  } else {
    setData((prevState) => ({ ...prevState, [id]: value }));
  }
}; */

/* const handleChange = (e) => {
  const { id, value } = e.target;
  const numericValue = parseFloat(value);

  setData(prev => {
    let updated = { ...prev };

    // 1. Normal fields (SKU, HSN, name etc.)
    if (!["cost_price", "margin_percent", "selling_price"].includes(id)) {
      updated[id] = value;
      return updated;
    }

    // 2. Numeric fields (cost, margin, selling)
    updated[id] = isNaN(numericValue) ? "" : numericValue;

    const cost = parseFloat(updated.cost_price);
    const margin = parseFloat(updated.margin_percent);
    const selling = parseFloat(updated.selling_price);

    // CASE 1 — Margin updated → update Selling Price
    if (id === "margin_percent" && cost > 0) {
      updated.selling_price = Number((cost * (1 + margin / 100)).toFixed(2));
    }

    // CASE 2 — Selling price updated → update Margin %
    if (id === "selling_price" && cost > 0) {
      updated.margin_percent = Number((((selling - cost) / cost) * 100).toFixed(2));
    }

    // CASE 3 — Cost updated → update selling using existing margin
    if (id === "cost_price" && cost > 0 && margin > 0) {
      updated.selling_price = Number((cost * (1 + margin / 100)).toFixed(2));
    }

    return updated;
  });
}; */
const handleChange = (e) => {
  const { id, value } = e.target;

    // handle nested fields for market_range and quantity_limits
 if (id.startsWith('market_range_')) {
    const key = id.split('_')[2]; // min or max
    setData(prev => {
      const updatedData = {
        ...prev,
        market_range: {
          ...prev.market_range,
          [key]: value
        }
      };
      setMarketRangeError('');
      
      // Validate market range
      const min = Number(updatedData.market_range.min) || 0;
      const max = Number(updatedData.market_range.max) || 0;
    if (min > 0 && max > 0 && max <= min) {
  setMarketRangeError('Maximum value must be greater than minimum value');
} else {
  setMarketRangeError('');
}
      
      return updatedData;
    });
    return;
  }

  if (id.startsWith('quantity_limits_')) {
    const key = id.split('_')[2];
    setData(prev => {
      const updatedData = {
        ...prev,
        quantity_limits: {
          ...prev.quantity_limits,
          [key]: value
        }
      };
      setQuantityLimitsError('');
      
      // Validate quantity limits
      const min = Number(updatedData.quantity_limits.min) || 0;
      const max = Number(updatedData.quantity_limits.max) || 0;
     if (min > 0 && max > 0 && max <= min) {
  setQuantityLimitsError('Maximum value must be greater than minimum value');
} else {
  setQuantityLimitsError('');
}
      
      return updatedData;
    });
    return;
  }

  setData(prev => {
    let updated = { ...prev };

    const isPriceField = ["cost_price", "margin_percent", "mrp", "discount", "selling_price"].includes(id);

    // If it's not a price field, just update normally
    if (!isPriceField) {
      updated[id] = value;
      return updated;
    }

    // Convert to number (but do NOT allow NaN to be stored)
    const numericValue = value === "" ? "" : Number(value);

    updated[id] = numericValue;

    // Extract values safely
    const mrp = Number(updated.mrp);
    const discount = Number(updated.discount);
    const cost = Number(updated.cost_price);
    const margin = Number(updated.margin_percent);
    const selling = Number(updated.selling_price);

    const hasValidMRP = !isNaN(mrp) && mrp > 0;
    const hasValidDiscount = !isNaN(discount) && discount >= 0 && discount <= 100;
    const hasValidCost = !isNaN(cost) && cost > 0;
    const hasValidMargin = !isNaN(margin) && margin >= 0;
    const hasValidSelling = !isNaN(selling) && selling >= 0;

    // ---------------------------------------------------
    // CASE 1 — MRP or Discount updated → recalculate Cost Price, then Selling Price
    // ---------------------------------------------------
    if (id === "mrp" || id === "discount") {
      if (hasValidMRP && hasValidDiscount) {
        updated.cost_price = Number((mrp * (1 - discount / 100)).toFixed(2));
        const newCost = Number(updated.cost_price);
        if (hasValidMargin && newCost > 0) {
          updated.selling_price = Number((newCost * (1 + margin / 100)).toFixed(2));
        }
      }
    }

    // ---------------------------------------------------
    // CASE 2 — Cost Price updated → recalculate Discount (if MRP exists), then Selling Price or Margin
    // ---------------------------------------------------
    if (id === "cost_price") {
      if (hasValidMRP && hasValidCost) {
        updated.discount = Number((((mrp - cost) / mrp) * 100).toFixed(2));
        // Ensure discount is not negative or over 100
        if (updated.discount < 0) updated.discount = 0;
        if (updated.discount > 100) updated.discount = 100;
      }
      if (hasValidCost && hasValidMargin) {
        updated.selling_price = Number((cost * (1 + margin / 100)).toFixed(2));
      } else if (hasValidCost && hasValidSelling) {
        updated.margin_percent = Number((((selling - cost) / cost) * 100).toFixed(2));
      }
    }

    // ---------------------------------------------------
    // CASE 3 — Margin updated → recalculate Selling Price
    // ---------------------------------------------------
    if (id === "margin_percent" && hasValidCost && hasValidMargin) {
      updated.selling_price = Number((cost * (1 + margin / 100)).toFixed(2));
    }

    // ---------------------------------------------------
    // CASE 4 — Selling Price updated → recalculate Margin
    // ---------------------------------------------------
    if (id === "selling_price" && hasValidCost && hasValidSelling) {
      updated.margin_percent = Number((((selling - cost) / cost) * 100).toFixed(2));
    }

    // SAFETY — Never allow NaN or undefined to reach state
    if (isNaN(updated.cost_price)) updated.cost_price = "";
    if (isNaN(updated.margin_percent)) updated.margin_percent = "";
    if (isNaN(updated.selling_price)) updated.selling_price = "";
    if (isNaN(updated.mrp)) updated.mrp = "";
    if (isNaN(updated.discount)) updated.discount = "";

    return updated;
  });
};


const handleCloseError = () => {
  setErrorMessages([]);
};

const closePopup = () => {
 // console.log("closePopup called");
  setShowPopup(false);
};

const Retry = async() => {
//  console.log("Retry called");
  setTimeout(async ()=>{
    if(retryFunction){

      await retryFunction ();
    }

    setIsLoading(false)
  },1000)
  
  setShowPopup(false);
};

const BackToLoginPage = () => {
  //console.log("Back To Login Page");
  setShowPopup(false);
  localStorage.removeItem('LoginData');
  sessionStorage.removeItem('userData');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('userId');
  sessionStorage.removeItem('appId');
navigate('/admin');
};

const handleStayHere = () => {
  setShowCancelPopup(false);
}

const handleCancel = () => {
  setShowCancelPopup(true);
}

const handleLeave = () => {
  navigate(-1);
}



  return (
    <div className="bg-white flex flex-row w-full justify-left h-full overflow-y-hidden">
  <div className="flex flex-row w-full">
    <div className="w-full flex-1">
      <div className="flex flex-col w-full h-full justify-center items-center">
        <div className="w-full h-full flex flex-col justify-between">
          <div className="w-full h-16 border-b flex items-center">
            <div className="flex w-full justify-between px-4 items-center">
              <h1 className="text-lg font-semibold">Edit Item</h1>
             {/*  <button className="text-white px-1 py-1 rounded-md">
                <FontAwesomeIcon icon={faX} className="text-md text-[#F7525A] cursor-pointer" />
              </button> */}
            </div>
          </div>
          {Object.keys(errors).length > 0 && (
        <div className="flex justify-between  w-full p-2 items-center px-auto py-3 bg-[#fddcde] rounded-md">
          <div className="px-6 flex flex-col space-y-2 justify-start items-start">
            {errors.vendor && (
              <span className="text-sm">
                • {errors.vendor}
              </span>
            )}
            {errors.product_category && (
              <span className="text-sm">
                • {errors.product_category}
              </span>
            )}
            {errors.product_name && (
              <span className="text-sm">
                • {errors.product_name}
              </span>
            )}
            {errors.UOM && (
              <span className="text-sm">
                • {errors.UOM}
              </span>
            )}
          </div>
          <div className='px-4'>
            <button
              className="text-gray-700 hover:text-red-500"
              onClick={() => setErrors({})}
            >
              <FontAwesomeIcon icon={faX} className="text-xs text-[#F7525A] cursor-pointer" />
            </button>
          </div>
        </div>
      )}
          <div className="flex flex-col justify-start items-start w-full flex-grow h-80 overflow-y-auto">         
                  <div className="w-full flex flex-col justify-start items-start px-4 bg-[#F9F9FB]">
                  <div className='w-full flex flex-row'>
            <div className="flex flex-col w-full">
              {/* Product Category Name Label and Input */}
              <div className=" w-full flex flex-row items-center mb-4 mt-5 space-x-20">
                <label htmlFor="productCategoryName" className="block text-sm font-regular text-[#e54643] w-1/6">
                  Item Category <span className="text-red-500">*</span>
                </label>           
                  <CustomDropdown
                    options={categoriesToDisplay}
                    selected={selectedProductCategory }
                    onSelect={(value, label) => handleProductCategorySelect(value, label)}
                    showAddNew={false}                 />
              </div>
              {/* Product Sub-Category Name Label and Input */}
              <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-20">
                <label htmlFor="productSubCategoryName" className="block text-sm font-regular text-gray-700 w-1/6">
                  Item Sub-Category 
                </label>
                
                <CustomDropdown
                options={(productSubCategories || []).map(subCategory => ({
                  value: subCategory._id,
                  label: subCategory.product_sub_category_name,
                }))}
                selected={selectedSubCategory}
                onSelect={(value, label) => handleProductSubcategory(value, label)}
                showAddNew={false}
                placeholder="Select Sub Category"
              />

               
              </div>
          
                     {/* vendor Label and Input */}
                     <div className="w-full flex flex-row items-center mb-4 space-x-20 mt-5">
                      <label htmlFor="productCategoryName" className="block text-sm font-regular text-[#e54643] w-1/6">
                        Vendor<span className="text-red-500">*</span>
                      </label>
                      <CustomDropdown
                        options={vendors}
                        selected={selectedVendor}
                        onSelect={handleVendorSelect}
                        showAddNew={false}
                        placeholder="Select Vendor"
                      />

                        <label className="flex items-center text-sm whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={includeAllVendors}
                            onChange={(e) => {
                              setIncludeAllVendors(e.target.checked);
                              if (selectedProductCategory?.id) {
                                fetchSubProductCategories(selectedProductCategory.id, e.target.checked);
                              }
                            }}
                          />
                          All
                        </label>

                    </div>
                     {/* Product Category Name Label and Input */}
                     <div className="w-full flex flex-row items-center mb-4 space-x-20 mt-5">
                      <label htmlFor="productCategoryName" className="block text-sm font-regular text-gray-700 w-1/6">
                        Item Group
                      </label>
                      <CustomDropdown
                    options={productGroups}
                    selected={selectedGroup }
                    onSelect={(value, label,id) => {
                      setSelectedGroup({
                        value: value,
                        label: label,
                        id: id,
                      });
                        console.log('Selected Group with ID:', { value, label, id }); // Debug

                    }}
                    showAddNew={false}
                  />

                    </div>
                    </div>
                    <div className='w-1/2 justify-start items-start mt-5'>
              {/* Image Upload Box */}
              <div className=" w-1/2 h-36 w-40  border-2 border-dashed border-gray-300 rounded-lg flex justify-center items-center relative">
                <input
                  type="file"
                  id="itemImage"
                  accept="image/*"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleImageUpload}
                />
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-32 h-28 object-cover rounded-lg"
                  />
                ) : (
                  <div className="text-gray-400 text-center">Click to upload image</div>
                )}
              </div>
              </div>
                    </div>
                    </div>
                    
                    <div className="w-full flex flex-col justify-start items-start px-4">
                     {/* Product Category Name Label and Input */}
                     <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
                      <label htmlFor="product_name" className="block text-sm font-regular text-[#e54643] w-1/6">
                        Item Name <span className="text-[#e54643]">*</span>
                      </label>
                      <input
                        type="text"
                        id="product_name"
                        className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
                       value={data.product_name}
                       onChange={handleChange}
                      
                      />
                    </div>

                     {/* Alternative Name */}
                     <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
                      <label htmlFor="alt_name" className="block text-sm font-regular text-gray-700 w-1/6">
                       Alternate Names
                      </label>
                      <textarea
                        id="alternate_names"
                        rows="3"
                        className="w-1/3 outline-none block rounded-md border border-gray-300 p-2 text-sm text-gray-700 resize-vertical"
                        value={data.alternate_names || ''}
                        onChange={handleChange}
                        placeholder="Enter Alternate Names"
                      />
                   </div>

                    {/* Product Category Name Label and Input */}
                    <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
                      <label htmlFor="SKU" className="block text-sm font-regular text-gray-700 w-1/6">
                       SKU
                      </label>
                      <input
                        type="text"
                        id="SKU"
                        className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
                       value={data.SKU}
                       onChange={handleChange}
                     
                      />
                    </div>
                    {/* Product Category Name Label and Input */}
                    <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
                      <label htmlFor="HSN_Code" className="block text-sm font-regular text-gray-700 w-1/6">
                        HSN code
                      </label>
                      <input
                        type="text"
                        id="HSN_Code"
                        className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
                       value={data.HSN_Code}
                       onChange={handleChange}
                      />
                    </div>

                
                    <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
                      <label htmlFor="uom" className="block text-sm font-regular text-[#e54643] w-1/6">UOM
                      <span className="text-[#e54643]">*</span></label>
                      <CustomDropdown
  options={uomOptions}
  selected={selectedUOM }
  onSelect={handleDropdownChange}
  labelField="label"
  valueField="_id"
  placeholder="Select UOM"
/>

                    </div>


      <div className="w-full flex flex-row items-center mb-4 space-x-2">
        <label htmlFor="isDeliveredByBrickbucket" className="block text-sm font-regular text-gray-700 w-1/6">
          Is Delivered By Brickbucket
        </label>
        <div className="w-1/3 relative">
          <select
            id="isDeliveredByBrickbucket"
            className="outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700 w-full appearance-none pr-8"
            value={isDeliveredByBrickbucket ? "true" : "false"}
            onChange={e => setIsDeliveredByBrickbucket(e.target.value === "true")}
          >
            <option value="false">False</option>
            <option value="true">True</option>
          </select>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="w-3 h-3 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
          />
        </div>
      </div>


      <div className="w-full flex flex-row items-center mb-4 space-x-2">
        <label htmlFor="isCODAvailable" className="block text-sm font-regular text-gray-700 w-1/6">
          Is COD Available
        </label>
        <div className="w-1/3 relative">
          <select
            id="isCODAvailable"
            className="outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700 w-full appearance-none pr-8"
            value={isCODAvailable ? "true" : "false"}
            onChange={e => setIsCODAvailable(e.target.value === "true")}
          >
            <option value="false">False</option>
            <option value="true">True</option>
          </select>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="w-3 h-3 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
          />
        </div>
      </div>


      <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
      <label htmlFor="tax" className="block text-sm text-red-500 font-regular text-gray-700 w-1/6">Tax Preference<span className="text-[#e54643]">*</span></label>
      <CustomDropdown
  options={Taxoptions}
  selected={selectedTax} 
  onSelect={(value, label) => handleTaxDropdownChange(value, label)}
  labelField="label"
  valueField="value"
  placeholder="Select Tax Preference"
/>


    </div>

     {/* Conditionally Render GST and IGST Fields */}
    
        <>
          <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
            <label htmlFor="GST" className="block text-sm font-regular text-gray-700 w-1/6">GST (%)<span className="text-[#e54643]">*</span></label>
            <input
              type="text"
              id="GST"
              className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
               value={data.GST || (selectedGroup?.GST ?? '')}
               onChange={ handleChange}
            />
          </div>
          <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
            <label htmlFor="IGST" className="block text-sm font-regular text-gray-700 w-1/6">IGST (%)<span className="text-[#e54643]">*</span></label>
            <input
              type="text"
              id="IGST"
              className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
               value={data.IGST || (selectedGroup?.IGST ?? '')}
               onChange={ handleChange}
            />
          </div>
                              <div className="w-full flex flex-row items-center mb-4 space-x-2">
                      <label htmlFor="isTaxIncluded" className="block text-sm font-regular text-gray-700 w-1/6">
                        Is Tax Included
                      </label>
                      <div className="w-1/3 relative">
                        <select
                          id="isTaxIncluded"
                          className="outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700 w-full appearance-none pr-8"
                          value={isTaxIncluded ? "true" : "false"}
                          onChange={e => setIsTaxIncluded(e.target.value === "true")}
                        >
                          <option value="false">False</option>
                          <option value="true">True</option>
                        </select>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className="w-3 h-3 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                        />
                      </div>
                    </div>
        </>
    

      <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
      <label htmlFor="mode" className="block text-sm text-red-500 font-regular text-gray-700 w-1/6">Purchase Mode<span className="text-[#e54643]">*</span></label>
      <CustomDropdown
        options={PurchaseMode}
        selected={selectedMode} 
        onSelect={(value, label) => handleModeDropdownChange(value, label)}
        labelField="label"
        valueField="value"
        placeholder="Select Purchase Mode"
      />


    </div>      

    {selectedMode?.label === 'Both' && (
  <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
    <label htmlFor="quote_threshold_quantity" className="block text-sm font-regular text-[#e54643] w-1/6">
      Eligible Quantity<span className="text-[#e54643]">*</span>
    </label>
    <input
      type="number"
      id="quote_threshold_quantity"
      className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
      value={quoteThresholdQuantity}
      onChange={(e) => setQuoteThresholdQuantity(e.target.value)}
      placeholder="Enter eligible quantity for quote"
      min="1"
    />
  </div>
)}   

    </div>

<div className="w-full flex flex-col justify-start items-start px-4">
<div className="w-full flex flex-row items-center mb-4 space-x-2">
  <label htmlFor="isTaxIncluded" className="block text-sm font-regular text-gray-700 w-1/6">
    Eligible For Fast Delivery
  </label>
  <div className="w-1/4 relative">
    <select
      id="fastDeliveryEligible"
      className="outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700 w-full appearance-none pr-8"
      value={fastDeliveryEligible ? "true" : "false"}
      onChange={e => setFastDeliveryEligible(e.target.value === "true")}
    >
      <option value="false">False</option>
      <option value="true">True</option>
    </select>
    <FontAwesomeIcon
      icon={faChevronDown}
      className="w-3 h-3 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
    />
  </div>
</div>

<div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5">
  <label htmlFor="maxFastDeliveryQty" className="block text-sm font-regular text-[#e54643] w-1/6">
    Max Fast Delivery Qty <span className="text-[#e54643]">*</span>
  </label>
  <input
    type="text"
    id="maxFastDeliveryQty"
    className="w-1/4 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
    value={maxFastDeliveryQty}
    onChange={(e) => setMaxFastDeliveryQty(e.target.value)}                      
  />
</div>

<div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
  <label htmlFor="minimumVehicleClass" className="block text-sm text-red-500 font-regular text-gray-700 w-1/6">Minimum Vehicle Class<span className="text-[#e54643]">*</span></label>
  <CustomDropdown
  options={vehicleClasses}
  selected={minimumVehicleClass}
  onSelect={handleMinimumVehicleClass}
  showAddNew={false}
/>
</div>

<div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
  <label htmlFor="handlingClass" className="block text-sm text-red-500 font-regular text-gray-700 w-1/6">Handling Class<span className="text-[#e54643]">*</span></label>
  <CustomDropdown
  options={handlingClasses}
  selected={handlingClass}
  onSelect={handleHandlingClass}
  showAddNew={false}
/>
</div>
</div>    

<div className="w-full flex flex-row items-start mb-4 mt-5">
  <label htmlFor="product_description" className="block text-sm font-regular text-gray-700 w-1/6 mt-2 ml-4">
    Description
  </label>
  <textarea
    id="product_description"
    rows="3"
    className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700 resize-vertical"
    value={data.product_description || ''}
    onChange={handleChange}
    placeholder="Enter product description..."
  />
</div>

  <div className="w-full mt-6 px-4">

  <h2 className="text-md font-semibold mb-3">Pricing Information</h2>

  <div className="flex flex-row items-center gap-6">

    {/* MRP */}
    <div className="flex flex-col w-1/3">
      <label htmlFor="mrp" className="text-sm text-red-500 mb-1">
        MRP<span className="text-[#e54643]">*</span>
      </label>
      <input
        type="text"
        id="mrp"
        value={data.mrp || selectedGroup?.mrp || ""}
        onChange={handleChange}
        className="outline-none rounded-md border border-gray-300 
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700"
      />
    </div>

    {/* MARGIN (%) */}
    <div className="flex flex-col w-1/3">
      <label htmlFor="discount" className="text-sm text-gray-700 mb-1">
        Discount (%)<span className="text-[#e54643]">*</span>
      </label>
      <input
        type="text"
        id="discount"
        value={data.discount ?? ""}
        onChange={handleChange}
        className="outline-none rounded-md border border-gray-300 
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700"
      />
    </div>

    {/* COST PRICE */}
    <div className="flex flex-col w-1/3">
      <label htmlFor="cost_price" className="text-sm text-red-500 mb-1">
        Cost Price<span className="text-[#e54643]">*</span>
      </label>
      <input
        type="text"
        id="cost_price"
        value={data.cost_price || selectedGroup?.costPrice || ""}
        onChange={handleChange}
        className="outline-none rounded-md border border-gray-300 
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700"
      />
{/*       {isPricingInvalid2 && (
        <div className="flex justify-end mt-1 text-xs text-red-600 font-medium">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-md mt-0.5 text-yellow-500 mr-1" />
          Cost price is lower than MRP.
        </div>
      )} */}
    </div>

    {/* MARGIN (%) */}
    <div className="flex flex-col w-1/3">
      <label htmlFor="margin_percent" className="text-sm text-gray-700 mb-1">
        Margin (%)<span className="text-[#e54643]">*</span>
      </label>
      <input
        type="text"
        id="margin_percent"
        value={data.margin_percent ?? ""}
        onChange={handleChange}
        className="outline-none rounded-md border border-gray-300 
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700"
      />
    </div>

    {/* SELLING PRICE */}
    <div className="flex flex-col w-1/3">
      <label htmlFor="selling_price" className="text-sm text-red-500 mb-1">
        Selling Price<span className="text-[#e54643]">*</span>
      </label>
      <input
        type="text"
        id="selling_price"
        value={data.selling_price || selectedGroup?.sellingPrice || ""}
        onChange={handleChange}
        className="outline-none rounded-md border border-gray-300 
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700"
      />
    </div>
  </div>
 {isPricingInvalid && (
    <div className="flex justify-end mt-1 text-xs text-red-600 font-medium">
      <FontAwesomeIcon icon={faTriangleExclamation} className="text-md mt-0.5 text-yellow-500 mr-1" />
      Selling price is lower than cost price.
    </div>
  )}
</div>

       

        {/* <div className="w-full flex flex-col px-4 mt-2 mb-2">
          <h2 className="text-md font-semibold mb-4">Buildcoins Information</h2>

          <div className="flex flex-row items-center mb-4 space-x-4">
            <label className="block text-sm font-regular text-gray-700 w-1/6">
              Is Buildcoins Applicable
            </label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="app"
                  value="yes"
                  checked={bcappStatus === "yes"}
                  onChange={handleBcAppChange}
                  className="text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-gray-700">Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="app"
                  value="no"
                  checked={bcappStatus === "no"}
                  onChange={handleBcAppChange}
                  className="text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-gray-700">No</span>
              </label>
            </div>
          </div>

          <div className="flex flex-row space-x-8">
            <div className="flex flex-row items-center w-1/2 space-x-4">
              <label htmlFor="max_quantity" className="block text-sm font-regular text-gray-700 w-1/3">
                Buildcoins Max Quantity Applicable
              </label>
              <input
                type="text"
                id="buildcoins_max_quantity_applicable"
                value={data.buildcoins_max_quantity_applicable || (selectedGroup?.buildcoins_max_quantity_applicable ?? 0)}
                onChange={handleChange}
                className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
              />
            </div>
            <div className="flex flex-row items-center w-1/2 space-x-4">
              <label htmlFor="max_discount" className="block text-sm font-regular text-gray-700 w-1/3">
                Buildcoins Max Discount (%)
              </label>
              <input
                type="text"
                id="buildcoins_max_discount"
                value={data.buildcoins_max_discount || (selectedGroup?.buildcoins_max_discount ?? 0)}
                onChange={handleChange}
                className="w-1/3 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
              />
            </div>
          </div>
        </div> */}


                <div className="w-full flex flex-col px-4 mt-4 mb-2">
                  <h2 className="text-md font-semibold mb-4">Market Range (price)</h2>
                  <div className="flex flex-row space-x-8">
                    <div className="flex flex-row items-center w-1/2 space-x-4">
                      <label htmlFor="max_quantity" className="block text-sm font-regular text-red-500 w-1/3">
                        Minimum<span className="text-[#e54643]">*</span>
                      </label>
                      <input
                        type="text"
                        id="market_range_min"
                        value={data.market_range.min}
                        onChange={handleChange}
 className={`w-1/3 outline-none block rounded-md border 
                   ${marketRangeError ? "border-red-500" : "border-gray-300"}
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700`}
                  />                    </div>
                    <div className="flex flex-row items-center w-1/2 space-x-4">
                      <label htmlFor="max_discount" className="block text-sm font-regular text-red-500 w-1/3">
                        Maximum<span className="text-[#e54643]">*</span>
                      </label>
                      <input
                        type="text"
                        id="market_range_max"
                        value={data.market_range.max}
                        onChange={handleChange}
                       className={`w-1/3 outline-none block rounded-md border 
                   ${marketRangeError ? "border-red-500" : "border-gray-300"}
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700`}
                  />
                    </div>
                  </div>
                   {marketRangeError && (
                      <div className="flex justify-end mt-1 text-xs text-red-600 font-medium mr-24">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="text-md mt-0.5 text-yellow-500 mr-1" />
                        {marketRangeError}
                      </div>
                    )}
                </div>



                <div className="w-full flex flex-col px-4 mt-2 mb-2">
                  <h2 className="text-md font-semibold mb-4">Quantity Limits</h2>
                  <div className="flex flex-row space-x-8">
                    <div className="flex flex-row items-center w-1/2 space-x-4">
                      <label htmlFor="max_quantity" className="block text-sm text-red-500 font-regular text-gray-700 w-1/3">
                        Minimum<span className="text-[#e54643]">*</span>
                      </label>
                      <input
                        type="text"
                        id="quantity_limits_min"
                        value={data.quantity_limits.min}
                        onChange={handleChange}
                       className={`w-1/3 outline-none block rounded-md border 
                   ${quantityLimitsError ? "border-red-500" : "border-gray-300"}
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700`}
                  />
                    </div>
                    <div className="flex flex-row items-center w-1/2 space-x-4">
                      <label htmlFor="max_discount" className="block text-sm text-red-500 font-regular text-gray-700 w-1/3">
                        Maximum<span className="text-[#e54643]">*</span>
                      </label>
                      <input
                        type="text"
                        id="quantity_limits_max"
                        value={data.quantity_limits.max}
                        onChange={handleChange}
                       className={`w-1/3 outline-none block rounded-md border 
                   ${quantityLimitsError ? "border-red-500" : "border-gray-300"}
                   hover:border-blue-500 focus:border-blue-500 
                   focus:ring focus:ring-blue-200 focus:ring-opacity-50 
                   p-2 text-sm text-gray-700`}
                  />
                    </div>
                  </div>
                   {quantityLimitsError && (
                      <div className="flex justify-end mt-1 text-xs text-red-600 font-medium mr-24">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="text-md mt-0.5 text-yellow-500 mr-1" />
                        {quantityLimitsError}
                      </div>
                    )}
                </div>

                                  <div className="w-full mt-6 mb-4 px-4">
                    <h2 className="text-md font-semibold mb-3">Weight & Dimensions</h2>
                      <div className="flex flex-row items-center gap-6">
                    {/* Weight */}
                      <div className="flex flex-col w-1/4">
                      <label htmlFor="weight" className="text-sm text-gray-700 mb-1">
                          Weight
                        </label>

                      <InputWithUnit
                        value={data.weight.value}
                        unit={data.weight.unit}
                        units={weightUnits}
                        onChange={(val) =>
                          setData(prev => ({
                            ...prev,
                            weight: { ...prev.weight, value: val }
                          }))
                        }
                        onUnitChange={(unit) =>
                          setData(prev => ({
                            ...prev,
                            weight: { ...prev.weight, unit }
                          }))
                        }
                      />
                    </div>

                    {/* Length */}
                      <div className="flex flex-col w-1/4">
                      <label htmlFor="length" className="text-sm text-gray-700 mb-1">
                          Length
                        </label>

                      <InputWithUnit
                        value={data.dimensions.length}
                        unit={data.dimensions.unit}
                        units={dimensionUnits}
                        onChange={(val) =>
                          setData(prev => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, length: val }
                          }))
                        }
                        onUnitChange={(unit) =>
                          setData(prev => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, unit }
                          }))
                        }
                      />
                    </div>

                    {/* Width */}
                      <div className="flex flex-col w-1/4">
                      <label htmlFor="width" className="text-sm text-gray-700 mb-1">
                          Width
                        </label>

                      <InputWithUnit
                        value={data.dimensions.width}
                        unit={data.dimensions.unit}
                        units={dimensionUnits}
                        onChange={(val) =>
                          setData(prev => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, width: val }
                          }))
                        }
                         onUnitChange={(unit) =>
                          setData(prev => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, unit }
                          }))
                        }
                      />
                    </div>

                    {/* Height */}
                      <div className="flex flex-col w-1/4">
                      <label htmlFor="height" className="text-sm text-gray-700 mb-1">
                          Height
                        </label>

                      <InputWithUnit
                        value={data.dimensions.height}
                        unit={data.dimensions.unit}
                        units={dimensionUnits}
                        onChange={(val) =>
                          setData(prev => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, height: val }
                          }))
                        }
                         onUnitChange={(unit) =>
                          setData(prev => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, unit }
                          }))
                        }
                      />
                    </div>
                  </div>
                  </div>

                  <div className="flex w-full justify-between px-4 items-center">
                  <h1 className="text-md font-semibold">Specifications</h1>           
                </div>

                <div className="w-full container mx-auto px-4 mb-2 mt-4">
                {rows.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="text-[#6c7184] bg-[#fafafc]">
                        <th className="font-semibold text-left text-sm py-2 px-2 border cursor-pointer border">SNo</th>
                        <th className="font-semibold text-left text-sm py-2 px-2 border cursor-pointer border">Name</th>
                        <th className="font-semibold text-left text-sm py-2 px-2 border cursor-pointer border">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                     {rows.map((row, index) => (
                            <tr key={index}>
                              <td className="py-2 px-4 border">{index + 1}</td>
                              <td className="border">
                              <input
                                  type="text"
                                  value={row.Name}
                                  onChange={(e) => handleInputChange(index, 'Name', e.target.value)}
                                  className="outline-blue-500 text-sm w-full h-full p-2 bg-transparent rounded-md hover:border-blue-500 hover:ring-1 hover:ring-blue-500 hover:ring-opacity-90"                                 
                                />
                              </td>
                              <td className="border">
                                <input
                                  type="text"
                                  value={row.Value}
                                  onChange={(e) => handleInputChange(index, 'Value', e.target.value)}
                                  className="outline-blue-500 text-sm w-full h-full p-2 bg-transparent rounded-md hover:border-blue-500 hover:ring-1 hover:ring-blue-500 hover:ring-opacity-90"
                                />
                              </td>
                              <td className="px-2">
                                <button
                                  type="button"
                                  onClick={() => deleteRow(index ,row)}
                                  className="text-red-500"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </td>
                            </tr>
                          ))} 
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-4">
                  <p>No Specifications</p>
                </div>
              )}
            </div>
            
                <div className="mt-4">
                  <button
                    onClick={addRow}
                    className="text-blue-500 py-2 px-4"
                  >
                    <FontAwesomeIcon icon={faPlusCircle} className="mr-2" />
                    Add Specifications
                  </button>
                </div>
                </div>
                    

                  <div className="mt-5 w-full">
                  <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
                  <div className="flex justify-start items-end space-x-2 px-6 mt-5 mb-4">
          <button className="px-2 py-1 flex-row bg-[#f7525a] border-[#f7525a] text-white rounded-md space-x-2 flex items-center"
           onClick={handleUpdateProduct} >
           <span>Save</span>
            {isSaving && <Spinner />}
            </button>
          <button onClick={handleCancel}  className={`hover:border-[#DDDDDD] focus:border-[#DDDDDD] focus:ring focus:ring-[#DDDDDD] focus:ring-opacity-50 px-2 py-1 rounded-md ${
          isSaving ? 'bg-gray-400 text-gray-500 cursor-not-allowed' : 'bg-[#f5f5f5] text-gray-700 border border-[#DDDDDD]'
        }`}
        disabled={isSaving}>
          Cancel</button>
        </div>
        </div>   
              </div>
            </div>
        
         </div> 

         {/* Popup starts */}

       

        {showNewUOMPopup && (
  <div className="fixed inset-0 z-50 flex items-top justify-center bg-gray-800 bg-opacity-50">
    <div className="bg-white rounded-b-md shadow-md w-1/2 h-2/3 flex flex-col animate-slide-in ">
      <div className='bg-[#F9F9FB] flex justify-between items-center w-full h-[10%] px-6'>
        <h2 className="text-md">New UOM</h2>
        <button onClick={handleClosePopup} className="text-white px-1 py-1 rounded-md ">   
          <FontAwesomeIcon icon={faX} className="text-xs text-[#e54643] cursor-pointer " />
        </button>
      </div>
      <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
      {errorMessages.length > 0 && (
        <div className='p-1'>
                <div className="mt-1 flex justify-between items-center  px-auto py-1  bg-[#fddcde]  rounded-md">
                  <div className="px-6 flex flex-col justify-start items-start">
                    {errorMessages.map((errorMessage, index) => (
                      <span key={index} className="text-sm">
                         •   {errorMessage}
                      </span>
                    ))}
                  </div>
                  <div className='px-4'>
                  <button
                    className="text-gray-700 hover:text-red-500"
                    onClick={handleCloseError}
                  >
                    <FontAwesomeIcon icon={faX} className="text-xs text-[#F7525A] cursor-pointer" />
                  </button>
                  </div>
                </div>
                </div>
              )}
      <div className="w-full flex flex-row items-center mb-4 space-x-2 mt-5 px-6">
        <label htmlFor="uom" className="block text-sm font-regular text-[#e54643] w-1/6">
          UOM<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="productCategoryName"
          className="w-[40%] outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
          value={UOM}
          onChange={(e)=>{setUOM(e.target.value)}}
       />
      </div>
      {/* Description Label and Textarea */}
      <div className="w-full flex flex-row items-center mb-4 space-x-2 px-6">
        <label htmlFor="Description" className="block text-sm font-regular text-gray-700 w-1/6">Description</label>
        <textarea
          id="Description"
          name="Description"
          className="mt-1 w-[40%] outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
          value={description}
          onChange={(e)=>{setdescription(e.target.value)}}
        />
      </div>
      <div className="mt-auto w-full">
        <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
        <div className="flex justify-start items-end space-x-2 px-6 mt-5 mb-4">
          <button className="px-2 py-1 bg-[#f7525a] border-[#f7525a] text-white rounded-md"
          onClick={handleSubmitUomData}>
            Save {isSaving && <Spinner />}
            </button>
          <button onClick={handleClosePopup} className="px-2 py-1 bg-[#f5f5f5] text-gray-700 border border-[#DDDDDD] rounded-md">Cancel</button>
        </div>
      </div>
      </div>
  </div>
)}

{showCancelPopup && (
    <div className="fixed inset-0 flex items-top justify-center bg-gray-800 bg-opacity-50 ">
    <div className="bg-white rounded-b-md shadow-md w-[550px] h-[250px] flex flex-col" style={{ maxWidth: '550px', maxHeight: '250px' }}>
    <div className='bg-[#F9F9FB] flex justify-between items-center w-full h-14 px-6'>
    <h2 className="text-md font-semibold">
    <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl text-yellow-500 mr-4 " />
      Leave this page?</h2>
      <button onClick={handleStayHere} className="text-white px-1 py-1 rounded-md ">   
        <FontAwesomeIcon icon={faX} className="text-xs text-[#212529] cursor-pointer " />
      </button>
    </div>
      <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
      <div className='pt-4 pl-4 '>
    <div className="w-full items-center p-5">
      <label htmlFor="Other_details" className="block text-sm font-regular text-[#212529] ">
      If you leave, your unsaved changes will be discarded.
      </label>
    </div>  
  </div>
      <div className="mt-auto w-full">
        <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
        <div className="text-sm flex justify-start items-end space-x-2 px-6 mt-5 mb-4">
        <button className="px-2 py-1 bg-[#408dfb] border-[#f7525a] text-white rounded-md" onClick={handleStayHere}>
  Stay Here  {isSaving && (
      <Spinner/>
    )}
  </button>
          <button onClick={handleLeave} 
          className="hover:border-[#DDDDDD] focus:border-[#DDDDDD] focus:ring focus:ring-[#DDDDDD] focus:ring-opacity-50  px-2 py-1 bg-[#f5f5f5] text-gray-700 border border-[#DDDDDD] rounded-md">
            Leave & Discard Changes</button>
        </div>
      </div>
    </div>
  </div>
)}

{showPopup && (
  <div>
    {popupType === '200or201' && <Msg200or201 message={popupMessage} timeout={3000} />}
    {popupType === '204' && <Msg204 message={popupMessage} timeout={3000} />}
    {popupType === '400' && <Msg400 message={popupMessage} closePopup={closePopup} />}
          {popupType === '401' && <Msg401 message={popupMessage} closePopup={closePopup} />}
          {popupType === '403' && <Msg403 message={popupMessage} closePopup={BackToLoginPage} />}
          {popupType === '500' && <Msg500 message={popupMessage} closePopup={Retry} cancelPopup={closePopup}/>}
  </div>
)}
 


      </div>
      </div>
  
  );
};

export default EditItems;
